import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mainSiteUrl } from '@/mocks/categories';
import { pillarArticlesA } from '@/mocks/pillarArticles';
import { pillarArticlesB } from '@/mocks/pillarArticles2';

const featureIcons = ['ri-search-line', 'ri-team-line', 'ri-ship-line', 'ri-shield-check-line'];
const allPillars = [...pillarArticlesA, ...pillarArticlesB];

export function Features() {
  const { t } = useTranslation();
  const items = [1, 2, 3, 4];

  return (
    <section className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="flex flex-col items-center text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t('features.title')}
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
            {t('features.subtitle')}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((n) => (
            <div key={n} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-900">
                <i className={`${featureIcons[n - 1]} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {t(`features.f${n}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {t(`features.f${n}.text`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ArticleTeaser() {
  const { t } = useTranslation();

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
      <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
        <div className="grid grid-cols-1 items-center lg:grid-cols-2">
          <div className="p-6 md:p-10">
            <span className="inline-block rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-900">
              {t('teaser.eyebrow')}
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t('teaser.title')}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t('teaser.text')}
            </p>
            <Link
              to="/usfans-spreadsheet-guide"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              {t('teaser.cta')}
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="flex h-56 items-center justify-center bg-primary-50 lg:h-full">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-book-open-line text-4xl" aria-hidden="true"></i>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function GuidesSection() {
  const { t, i18n } = useTranslation();

  return (
    <section className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="flex flex-col items-center text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t('guides.title')}
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
            {t('guides.subtitle')}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {allPillars.map((pillar) => {
            const lang = pillar.content[i18n.language] ? i18n.language : 'en';
            const content = pillar.content[lang];
            return (
              <Link
                key={pillar.slug}
                to={`/guides/${pillar.slug}`}
                className="group flex flex-col rounded-lg border border-background-200/70 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-900">
                  <i className={`${pillar.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold leading-snug text-foreground-950">
                  {content.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                  {content.excerpt}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 transition-colors group-hover:text-primary-600 whitespace-nowrap">
                  {t('guides.readMore')}
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(0);
  const questions = [1, 2, 3, 4];

  return (
    <section className="bg-background-100">
      <div className="mx-auto max-w-3xl px-4 py-14 md:px-6 md:py-16">
        <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {t('faq.title')}
        </h2>

        <div className="mt-8 space-y-3">
          {questions.map((n) => {
            const isOpen = open === n;
            return (
              <div key={n} className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : n)}
                  className="flex w-full items-center justify-between px-4 py-4 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <span className="text-sm font-semibold text-foreground-900 md:text-base">
                    {t(`faq.q${n}`)}
                  </span>
                  <i
                    className={`ri-add-line text-xl text-foreground-500 transition-transform ${isOpen ? 'rotate-45' : ''}`}
                    aria-hidden="true"
                  ></i>
                </button>
                {isOpen && (
                  <div className="border-t border-background-200/70 px-4 py-4">
                    <p className="text-sm leading-relaxed text-foreground-600">{t(`faq.a${n}`)}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FinalCta() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-800"></div>
      <div className="relative mx-auto flex max-w-4xl flex-col items-center px-4 py-16 text-center md:px-6 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
          {t('cta.title')}
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-background-100/90 md:text-base">
          {t('cta.text')}
        </p>
        <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="w-full rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 sm:w-auto whitespace-nowrap"
          >
            {t('cta.primary')}
          </a>
          <Link
            to="/usfans-spreadsheet-guide"
            className="w-full rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-100 sm:w-auto whitespace-nowrap"
          >
            {t('cta.secondary')}
          </Link>
        </div>
      </div>
    </section>
  );
}